import React, { useEffect, useMemo, useState } from 'react';
import PageContainer from '../components/PageContainer';
import { getWhatsApp, saveWhatsApp, WhatsAppConfig, WhatsAppOverview } from '../api/whatsapp';

/* Official WhatsApp glyph (brand asset, Meta brand guidelines). Brand green
   #25D366 is used ONLY here and on scope status — everywhere else the icon
   is monochrome to stay inside the design system. */
export const WhatsAppGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const EMPTY: WhatsAppConfig = {
  enabled: false, display_name: '', phone_number: '', phone_number_id: '',
  waba_id: '', business_id: '', access_token: '', app_secret: '',
  webhook_verify_token: '', api_version: 'v23.0',
};

const E164 = /^\+[1-9]\d{6,14}$/;
const NUMERIC_ID = /^\d{10,20}$/;

function validate(c: WhatsAppConfig): Record<string, string> {
  const errs: Record<string, string> = {};
  const phone = c.phone_number.replace(/[\s-]/g, '');
  if (phone && !E164.test(phone)) errs.phone_number = 'Use international E.164 format, e.g. +970599123456';
  if (c.phone_number_id && !NUMERIC_ID.test(c.phone_number_id)) errs.phone_number_id = 'Meta IDs are 10-20 digit numbers';
  if (c.waba_id && !NUMERIC_ID.test(c.waba_id)) errs.waba_id = 'Meta IDs are 10-20 digit numbers';
  if (c.business_id && !NUMERIC_ID.test(c.business_id)) errs.business_id = 'Meta IDs are 10-20 digit numbers';
  if (c.webhook_verify_token && c.webhook_verify_token.length < 16) errs.webhook_verify_token = 'At least 16 characters';
  if (c.api_version && !/^v\d{1,3}\.\d{1,2}$/.test(c.api_version)) errs.api_version = 'Looks like v23.0';
  if (c.enabled) {
    if (!phone) errs.phone_number = errs.phone_number || 'Required to enable';
    if (!c.phone_number_id) errs.phone_number_id = errs.phone_number_id || 'Required to enable';
    if (!c.waba_id) errs.waba_id = errs.waba_id || 'Required to enable';
    if (!c.has_access_token && (!c.access_token || c.access_token.startsWith('••'))) errs.access_token = 'Required to enable';
    if (!c.webhook_verify_token) errs.webhook_verify_token = 'Required to enable';
  }
  return errs;
}

const Field: React.FC<{
  label: string; hint?: string; error?: string; children: React.ReactNode;
}> = ({ label, hint, error, children }) => (
  <div>
    <label className="label">{label}</label>
    {children}
    {error
      ? <p className="mt-1 text-xs text-error-dark">{error}</p>
      : hint ? <p className="mt-1 text-xs text-primary-400">{hint}</p> : null}
  </div>
);

const ScopeCard: React.FC<{
  title: string; subtitle: string; accent?: string | null;
  domainId: number | null; webhookPath: string;
  initial: WhatsAppConfig; suggestedToken: string;
}> = ({ title, subtitle, accent, domainId, webhookPath, initial, suggestedToken }) => {
  const [cfg, setCfg] = useState<WhatsAppConfig>({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [serverError, setServerError] = useState('');
  const [copied, setCopied] = useState(false);
  const errors = useMemo(() => validate(cfg), [cfg]);
  const webhookUrl = `${window.location.origin}${webhookPath}`;

  const set = (k: keyof WhatsAppConfig, v: string | boolean) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setSaved(false); setServerError('');
  };

  const configured = cfg.has_access_token && cfg.phone_number_id && cfg.waba_id;

  const onSave = async () => {
    if (Object.keys(errors).length) return;
    setSaving(true); setServerError('');
    try {
      const res = await saveWhatsApp(domainId, cfg);
      setCfg({ ...EMPTY, ...res.config });
      setSaved(true);
    } catch (e: any) {
      setServerError(e?.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-primary-100">
        <span
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0"
          style={{ background: accent || '#8e8ea0' }}
        >
          <WhatsAppGlyph className="w-[18px] h-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold text-primary-900">{title}</div>
          <div className="text-xs text-primary-400">{subtitle}</div>
        </div>
        {cfg.enabled
          ? <span className="badge bg-[#E7F9EF] text-[#128C4B] ring-1 ring-inset ring-[#25D366]/30">Enabled</span>
          : configured
            ? <span className="badge-info">Configured, off</span>
            : <span className="badge-neutral">Not connected</span>}
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Display name" hint="Shown to users in WhatsApp; must match the approved Meta display name.">
          <input className="input-base" value={cfg.display_name} maxLength={120}
                 onChange={(e) => set('display_name', e.target.value)} placeholder="An-Najah Assistant" />
        </Field>
        <Field label="Business phone number" error={errors.phone_number} hint="E.164 format. Must not be an active personal WhatsApp number.">
          <input className="input-base font-mono" dir="ltr" value={cfg.phone_number} maxLength={20}
                 onChange={(e) => set('phone_number', e.target.value)} placeholder="+970599123456" />
        </Field>
        <Field label="Phone number ID" error={errors.phone_number_id} hint="From Meta App dashboard → WhatsApp → API Setup.">
          <input className="input-base font-mono" dir="ltr" value={cfg.phone_number_id} maxLength={32}
                 onChange={(e) => set('phone_number_id', e.target.value)} placeholder="123456789012345" />
        </Field>
        <Field label="WhatsApp Business Account ID (WABA)" error={errors.waba_id}>
          <input className="input-base font-mono" dir="ltr" value={cfg.waba_id} maxLength={32}
                 onChange={(e) => set('waba_id', e.target.value)} placeholder="123456789012345" />
        </Field>
        <Field label="Meta Business ID" hint="Optional." error={errors.business_id}>
          <input className="input-base font-mono" dir="ltr" value={cfg.business_id} maxLength={32}
                 onChange={(e) => set('business_id', e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Graph API version" error={errors.api_version}>
          <input className="input-base font-mono" dir="ltr" value={cfg.api_version} maxLength={12}
                 onChange={(e) => set('api_version', e.target.value)} placeholder="v23.0" />
        </Field>
        <Field label="Permanent access token" error={errors.access_token}
               hint={cfg.has_access_token ? 'A token is stored. Leave as is to keep it, or paste a new one to replace.' : 'System-user token with whatsapp_business_messaging scope.'}>
          <input className="input-base font-mono" dir="ltr" type="password" value={cfg.access_token}
                 onChange={(e) => set('access_token', e.target.value)}
                 placeholder={cfg.has_access_token ? cfg.access_token || '••••' : 'EAAG…'} />
        </Field>
        <Field label="App secret" hint={cfg.has_app_secret ? 'Stored. Optional; used to verify webhook signatures.' : 'Optional; used to verify webhook signatures.'}>
          <input className="input-base font-mono" dir="ltr" type="password" value={cfg.app_secret}
                 onChange={(e) => set('app_secret', e.target.value)}
                 placeholder={cfg.has_app_secret ? cfg.app_secret || '••••' : 'From Meta App → Settings → Basic'} />
        </Field>
        <Field label="Webhook verify token" error={errors.webhook_verify_token}
               hint="Any secret string; Meta echoes it during webhook verification.">
          <div className="flex gap-2">
            <input className="input-base font-mono" dir="ltr" value={cfg.webhook_verify_token} maxLength={128}
                   onChange={(e) => set('webhook_verify_token', e.target.value)} />
            <button type="button" className="btn-secondary whitespace-nowrap"
                    onClick={() => set('webhook_verify_token', suggestedToken)}>
              Generate
            </button>
          </div>
        </Field>
        <Field label="Callback URL" hint="Paste this in Meta App → WhatsApp → Configuration → Webhook.">
          <div className="flex gap-2">
            <input className="input-base font-mono bg-primary-50" dir="ltr" readOnly value={webhookUrl} />
            <button type="button" className="btn-secondary whitespace-nowrap"
                    onClick={() => { navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </Field>
      </div>

      <div className="px-5 py-4 border-t border-primary-100 flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <button
            type="button" role="switch" aria-checked={cfg.enabled}
            onClick={() => set('enabled', !cfg.enabled)}
            className={`w-10 h-[22px] rounded-full transition-colors relative flex-shrink-0
              ${cfg.enabled ? 'bg-[#25D366]' : 'bg-primary-200'}`}
          >
            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow transition-all
              ${cfg.enabled ? 'start-[21px]' : 'start-[3px]'}`} />
          </button>
          <span className="text-sm font-medium text-primary-700">Enable WhatsApp for this assistant</span>
        </label>
        <div className="flex-1" />
        {serverError && <span className="text-xs text-error-dark">{serverError}</span>}
        {saved && !serverError && <span className="text-xs text-success-dark">Saved</span>}
        <button className="btn-primary" onClick={onSave}
                disabled={saving || Object.keys(errors).length > 0}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

const SETUP_STEPS: { title: string; body: string }[] = [
  { title: 'Meta Business account, verified', body: 'business.facebook.com → Settings → Business verification. This review (2-4 days) is the bottleneck; start it first.' },
  { title: 'WhatsApp Business Account (WABA)', body: 'Created automatically when WhatsApp is added to a Meta App; note the WABA ID.' },
  { title: 'Dedicated phone number', body: 'A number not registered on personal WhatsApp, able to receive an SMS or voice verification code. Display name goes through Meta review.' },
  { title: 'Meta App with the WhatsApp product', body: 'developers.facebook.com → Create App (Business type) → add WhatsApp. The API Setup page shows the Phone number ID and WABA ID.' },
  { title: 'Permanent access token', body: 'Business Settings → System Users → create, grant the app whatsapp_business_messaging + whatsapp_business_management, generate token. Never use the 24-hour test token in production.' },
  { title: 'Webhook subscription', body: 'App → WhatsApp → Configuration: paste the Callback URL and verify token from this page, subscribe to the "messages" field.' },
];

const WhatsApp: React.FC = () => {
  const [data, setData] = useState<WhatsAppOverview | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getWhatsApp().then(setData).catch(() => setError('Could not load WhatsApp settings.'));
  }, []);

  return (
    <PageContainer
      title="WhatsApp"
      subtitle="Connect each assistant to the WhatsApp Business Platform (Meta Cloud API)."
    >
      {error && <div className="card p-4 text-sm text-error-dark">{error}</div>}
      {!data && !error && <div className="card p-8 text-center text-primary-400 text-sm">Loading…</div>}
      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr,320px] gap-6 items-start">
          <div className="space-y-5">
            <ScopeCard
              title="General chatbot" subtitle="University-wide assistant"
              accent="#8e8ea0" domainId={null}
              webhookPath={data.general.webhook_path}
              initial={data.general.config}
              suggestedToken={data.suggested_verify_token}
            />
            {data.domains.map((d) => (
              <ScopeCard
                key={d.domain_id}
                title={d.name_en} subtitle={`Domain assistant · /c/${d.slug}`}
                accent={d.primary_color} domainId={d.domain_id}
                webhookPath={d.webhook_path}
                initial={d.config}
                suggestedToken={data.suggested_verify_token}
              />
            ))}
          </div>

          <aside className="card p-5 xl:sticky xl:top-6">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-8 h-8 rounded-lg bg-[#25D366] text-white flex items-center justify-center">
                <WhatsAppGlyph className="w-4 h-4" />
              </span>
              <div className="text-sm font-semibold text-primary-900">Connection guide</div>
            </div>
            <ol className="space-y-3.5">
              {SETUP_STEPS.map((st, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-500 text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div>
                    <div className="text-xs font-semibold text-primary-800">{st.title}</div>
                    <div className="text-xs text-primary-500 mt-0.5 leading-relaxed">{st.body}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="hairline my-4" />
            <p className="text-xs text-primary-400 leading-relaxed">
              Message sending and the webhook receiver ship in a later phase; completing this
              configuration (and Meta's business verification) now means activation is a switch flip later.
              New numbers start at 1,000 business-initiated conversations / 24h and scale automatically.
            </p>
          </aside>
        </div>
      )}
    </PageContainer>
  );
};

export default WhatsApp;
